/**
 * Copyright Highway9 Networks Inc.
 */
import { AuditLogResponse } from "~/types/auditLog";
import { authenticationService } from ".";
import APIService from "./APIServices";
import { Permission, UserInfo } from "~/types/user";

const userAPI = new APIService<UserInfo>("users");

export interface Role {
  id: string;
  name: string;
  permissions: Permission[];
  type: string;
}

export const userService = {
  getAuditLogs,
  updateUserProfile,
  getUserInfoByID,
  updateUserInfo: userAPI.update,
  getUserInfo,
  getUserPermissions,
  getUserRoles,
  get currentUserInfo() {
    return getUserInfoLocalStorage();
  },
  createUser: userAPI.create,
  getAllUser : userAPI.getAll,
  deleteUser: userAPI.delete,
  updateUser: userAPI.update,
  getUserResetPasswordLink,
  queryUserInfoByOtp,
};

export type QueryUserInfoByOtpParams = {
  userName: string;
  otp: string;
};

async function queryUserInfoByOtp(params: QueryUserInfoByOtpParams) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(params),
  };

  const token = authenticationService.currentUserValue?.token;
  if (token) {
    (requestOptions.headers as Record<string, string>)["Authorization"] = "Bearer " + token;
  }

  const response = await fetch("/api/v1/saas/users/userInfo/query", requestOptions);
  const text = await response.text();
  const result = text && JSON.parse(text);
  const data = result?.data ?? result;
  return data as {
    countryCode: string;
    phoneNumber: string;
    otpConsentGranted: boolean;
  };
}

async function getAuditLogs(filters: any) {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      Authorization: "Bearer " + authenticationService.currentUserValue?.token,
    },
    body: JSON.stringify(filters),
  };
  const logs = await fetch("/api/v1/orch/auditLogs/query", requestOptions).then((res) => res.json());
  return logs as AuditLogResponse;
}

async function updateUserProfile(data: any) {
  const profile = await userAPI.post("profile", data);
  return profile as UserInfo;
}
async function getUserInfoByID(id: string) {
  const profile = await userAPI.get(id);
  return profile;
}

async function getUserInfo() {
  const currentUser = authenticationService.currentUserValue;
  if (!currentUser) return null;
  const userInfo = await userService.getUserInfoByID(currentUser.userId);
  localStorage.setItem("userInfo", JSON.stringify(userInfo));

  return userInfo;
}

function getUserInfoLocalStorage() {
  if (typeof window !== "undefined") {
    const state = localStorage.getItem("userInfo");
    if (state) {
      return JSON.parse(state) as UserInfo;
    } else {
      return null;
    }
  }
  return null;
}

async function getUserPermissions() {
  const currentUser = authenticationService.currentUserValue;
  if (!currentUser) return null;
  const permissions = await userAPI.get(`${currentUser.userId}/permissions`) as unknown as Permission[];
  return permissions;
}

async function getUserRoles() {
  return userAPI.get("roles") as unknown as Role[];
}

async function getUserResetPasswordLink(email: string) {
  const operation = {
      "name": "user-forgot-password",
      "type": "operation",
      "operationType": "FORGOT_PASSWORD",
      "objectType": "user",
      "input": [
          {
              "key": "userName",
              "value": email
          }
      ]
  }

  const requestOptions = {
      method: "POST",
      headers: {
          "Content-Type": "application/json",
          accept: "application/json",
          Authorization: "Bearer " + authenticationService.currentUserValue?.token,
      },
      body: JSON.stringify(operation),
  };

  const response = await fetch('/api/v1/orch/operations/forgotPassword', requestOptions).then(res => res.json());

  return response as {
      input : null,
      output : {
          key : string,
          value : string
      }[],
   };
}
